import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.message.length > 500) {
      alert('Message must be 500 characters or less');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d51tfuja2jsd7vukfvm0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-5 gap-0 rounded-[40px] overflow-hidden shadow-2xl">
          <div className="lg:col-span-3 bg-[#1A1F36] p-12 lg:p-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-12">
              GET IN TOUCH
            </h2>

            <div className="space-y-8">
              <div className="flex items-center gap-5">
                <div className="w-14 h-14 bg-[#00D4AA] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-white text-2xl"></i>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Email</p>
                  <a href="mailto:contact@salinassrl.com" className="text-white text-lg hover:text-[#00D4AA] transition-colors">
                    contact@salinassrl.com
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-5">
                <div className="w-14 h-14 bg-[#00D4AA] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-white text-2xl"></i>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Phone</p>
                  <a href="tel:+15707156139" className="text-white text-lg hover:text-[#00D4AA] transition-colors">
                    +1 5707156139
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-5">
                <div className="w-14 h-14 bg-[#00D4AA] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-white text-2xl"></i>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Address</p>
                  <p className="text-white text-lg leading-relaxed">
                    BV. RACEDO 599<br />
                    Paraná, E3100IDT<br />
                    Argentina
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 bg-gradient-to-br from-[#00D4AA]/20 to-[#00BF99]/10 p-12 lg:p-16 flex items-center justify-center">
            <form onSubmit={handleSubmit} className="w-full space-y-6" data-readdy-form id="contact-form">
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name"
                  required
                  className="w-full px-5 py-3 rounded-lg border-2 border-gray-300 focus:border-[#00D4AA] focus:outline-none text-sm"
                />
              </div>

              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Your Email"
                  required
                  className="w-full px-5 py-3 rounded-lg border-2 border-gray-300 focus:border-[#00D4AA] focus:outline-none text-sm"
                />
              </div>

              <div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Your Phone"
                  className="w-full px-5 py-3 rounded-lg border-2 border-gray-300 focus:border-[#00D4AA] focus:outline-none text-sm"
                />
              </div>

              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Your Message (max 500 characters)"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-5 py-3 rounded-lg border-2 border-gray-300 focus:border-[#00D4AA] focus:outline-none resize-none text-sm"
                ></textarea>
                <p className="text-xs text-gray-600 mt-1">{formData.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#1A1F36] text-white py-3 rounded-lg font-semibold hover:bg-[#252B45] transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg text-sm">
                  Message sent successfully! We'll get back to you soon.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg text-sm">
                  Failed to send message. Please try again.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
